/**
 * Template Dropdown Component
 *
 * Compact dropdown for NoteTopBar that displays the current template
 * and allows users to select a different template for regeneration.
 *
 * Features:
 * - Shows current template name in compact format
 * - Dropdown menu with all available templates
 * - Analytics tracking for interactions
 * - Loading state support
 *
 * V2 Migration:
 * - Uses V2 API via useGetTemplatesQuery
 * - Maps to TemplatePickerItem for backward compatibility
 */

import React, { useMemo, useState } from 'react';
import { type TemplatePickerItem } from '@components/Pickers/TemplatePicker';
import { useGetTemplatesQuery } from '@features/templates';
import {
  KeyboardArrowDown as ArrowDownIcon,
  PushPin,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  Box,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';

export interface TemplateDropdownProps {
  /**
   * Current template ID
   */
  currentTemplateId?: string;

  /**
   * Current template display name
   */
  currentTemplateName?: string;

  /**
   * Callback when user selects a different template
   */
  onTemplateSelect: (template: TemplatePickerItem) => void;

  /**
   * Note ID for analytics tracking
   */
  noteId?: string;

  /**
   * Encounter ID for analytics tracking
   */
  encounterId?: string;

  /**
   * Disabled state
   */
  disabled?: boolean;
}

/**
 * Compact template dropdown for NoteTopBar
 */
export const TemplateDropdown: React.FC<TemplateDropdownProps> = ({
  currentTemplateId,
  currentTemplateName,
  onTemplateSelect,
  noteId,
  encounterId,
  disabled = false,
}) => {
  const { data: templatesData, isLoading } = useGetTemplatesQuery();
  const { note: noteMetrics } = useEventTracking();
  const [isOpen, setIsOpen] = useState(false);

  // Transform V2 templates to legacy-compatible format and sort by pin status
  const templatesList = useMemo<TemplatePickerItem[]>(() => {
    if (!templatesData?.templates) {
      return [];
    }

    const templates = templatesData.templates.map(
      (item): TemplatePickerItem => ({
        template_id: item.template.id,
        display_name: item.revision.title,
        // Map owner_scope to legacy 'type' field for backward compatibility
        type: item.template.owner_scope === 'user' ? 'user' : 'public',
        owner_scope: item.template.owner_scope,
        displayOrder: item.display_order ?? null,
      }),
    );

    // Sort: pinned templates first (by displayOrder), then unpinned alphabetically
    return templates.sort((a, b) => {
      const aIsPinned = a.displayOrder !== null && a.displayOrder !== undefined;
      const bIsPinned = b.displayOrder !== null && b.displayOrder !== undefined;

      if (aIsPinned && !bIsPinned) return -1;
      if (!aIsPinned && bIsPinned) return 1;
      if (aIsPinned && bIsPinned) return (a.displayOrder ?? 0) - (b.displayOrder ?? 0);
      return a.display_name.localeCompare(b.display_name);
    });
  }, [templatesData]);

  // Check if current template is in the user's list
  // If not, we can't use it as the Select value (would cause MUI warning)
  const isCurrentTemplateInList = useMemo(() => {
    if (!currentTemplateId) return false;
    return templatesList.some(t => t.template_id === currentTemplateId);
  }, [currentTemplateId, templatesList]);

  const handleOpen = () => {
    setIsOpen(true);

    // Track dropdown opened
    if (noteId) {
      noteMetrics.templateDropdownOpened(noteId, {
        current_template_id: currentTemplateId,
        current_template_name: currentTemplateName,
        encounter_id: encounterId,
      });
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleChange = (event: SelectChangeEvent) => {
    const selectedTemplateId = event.target.value;

    // Find the selected template object
    const selectedTemplate = templatesList.find(t => t.template_id === selectedTemplateId);

    if (selectedTemplate) {
      onTemplateSelect(selectedTemplate);
    }

    handleClose();
  };

  // Show loading state if templates not loaded yet
  if (isLoading || templatesList.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1,
          borderRadius: 1,
          bgcolor: 'backgroundColors.secondary',
          border: '0.1px solid',
          borderColor: 'borderColors.lightprimary',
        }}
      >
        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
          Loading templates...
        </Typography>
      </Box>
    );
  }

  return (
    <FormControl size="small" disabled={disabled}>
      <Select
        value={isCurrentTemplateInList ? currentTemplateId : ''}
        onChange={handleChange}
        onOpen={handleOpen}
        onClose={handleClose}
        open={isOpen}
        displayEmpty
        IconComponent={ArrowDownIcon}
        sx={{
          fontSize: '0.85rem',
          border: '1px solid',
          borderColor: 'borderColors.primary',
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
          minWidth: 180,
          maxWidth: 300,
        }}
        renderValue={() => {
          // Always display the current template name, even if not in user's list
          if (!currentTemplateName) {
            return (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Select template
              </Typography>
            );
          }

          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                maxWidth: '100%',
                overflow: 'hidden',
              }}
            >
              <Typography
                variant="body2"
                component="span"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                }}
              >
                {currentTemplateName}
              </Typography>
            </Box>
          );
        }}
      >
        {templatesList.map(template => {
          const isCurrentTemplate = template.template_id === currentTemplateId;

          const handleRegenerateClick = (e: React.MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();
            handleClose();
            onTemplateSelect(template);
          };

          return (
            <MenuItem
              key={template.template_id}
              value={template.template_id}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 1,
                minWidth: 0, // Allow flex item to shrink below content size
                // Style current template to look disabled but keep it interactive for the button
                ...(isCurrentTemplate && {
                  opacity: 0.7,
                  pointerEvents: 'auto',
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }),
              }}
              // Prevent selecting the current template via click on the menu item text
              onClick={isCurrentTemplate ? e => e.preventDefault() : undefined}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  py: 0.5,
                  minWidth: 0, // Allow flex item to shrink below content size
                  flex: 1, // Take available space
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {template.displayOrder !== null && template.displayOrder !== undefined && (
                    <PushPin sx={{ fontSize: 16, color: 'primary.light', mr: 1 }} />
                  )}
                  <Typography
                    variant="body2"
                    sx={{
                      color: isCurrentTemplate ? 'text.secondary' : 'text.primary',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '100%',
                    }}
                  >
                    {template.display_name}
                  </Typography>
                </Box>
                {isCurrentTemplate && (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    (Current)
                  </Typography>
                )}
              </Box>
              {isCurrentTemplate && (
                <Tooltip title="Regenerate with this template">
                  <IconButton
                    size="small"
                    onClick={handleRegenerateClick}
                    sx={{
                      ml: 1,
                      color: 'primary.main',
                      opacity: 1,
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};
